import React, { useState } from 'react';
import styled from 'styled-components';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';

import firebase from '../../Firebase/Firebase';
import { getCurrentPosition, toCoordinate } from '../../Utils/geo';
import { readPrivateDemandLocation } from '../../Utils/demandLocation';
import { useI18n } from '../../i18n/I18nProvider';

const timestamp = (value) => value?.toMillis?.() || (value?.seconds ? value.seconds * 1000 : new Date(value || 0).getTime());

const RoutePlannerButton = ({ projects = [] }) => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const planRoute = async () => {
    setLoading(true);
    setMessage('');
    try {
      const candidates = projects
        .filter((project) => ['estimation_confirmed', 'estimation_visit_valid', 'assigned'].includes(project.demand_status))
        .sort((a, b) => timestamp(a.estimation?.visit_date || a.contract?.start_date) - timestamp(b.estimation?.visit_date || b.contract?.start_date))
        .slice(0, 9);
      if (candidates.length === 0) throw new Error(t('location.noRoute'));

      const locationResults = await Promise.allSettled(candidates.map((project) => (
        readPrivateDemandLocation(firebase.firestore(), project.demand_id)
      )));
      const stops = locationResults
        .map((result, index) => ({
          project: candidates[index],
          data: result.status === 'fulfilled' ? result.value?.data || null : null,
        }))
        .filter((item) => toCoordinate(item.data));
      if (stops.length === 0) throw new Error(t('location.exactNotAccessible'));

      let origin = '';
      try {
        const current = await getCurrentPosition({ maximumAge: 120000, enableHighAccuracy: false });
        origin = `${current.latitude},${current.longitude}`;
      } catch (_) {
        origin = '';
      }

      const destinationCoordinate = toCoordinate(stops[stops.length - 1].data);
      const destination = `${destinationCoordinate.latitude},${destinationCoordinate.longitude}`;
      const waypoints = stops.slice(0, -1).map(({ data }) => {
        const coordinate = toCoordinate(data);
        return `${coordinate.latitude},${coordinate.longitude}`;
      });
      const params = new URLSearchParams({ api: '1', destination, travelmode: 'driving', utm_source: 'lepetitboulot' });
      if (origin) params.set('origin', origin);
      if (waypoints.length) params.set('waypoints', waypoints.join('|'));
      window.open(`https://www.google.com/maps/dir/?${params.toString()}`, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setMessage(error.message || t('location.routeError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Button type="button" onClick={planRoute} disabled={loading}>
        <AltRouteOutlinedIcon /> {loading ? t('location.preparing') : t('location.planVisits')}
      </Button>
      {message && <Message>{message}</Message>}
    </Wrapper>
  );
};

const Wrapper = styled.div`display: grid; justify-items: end; gap: .3rem;`;
const Button = styled.button`display: inline-flex; align-items: center; gap: .45rem; min-height: 2.75rem; padding: .62rem .85rem; border: 1px solid #c7d7e0; border-radius: 999px; background: #fff; color: #111111; font: inherit; font-size: .8rem; font-weight: 850; cursor: pointer; &:disabled { opacity: .55; cursor: wait; } svg { font-size: 1.05rem; }`;
const Message = styled.span`max-width: 22rem; color: #7b5520; font-size: .7rem; text-align: right;`;

export default RoutePlannerButton;
